import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { Search, Trash2, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';

const LOCK_MS = 60 * 60 * 1000; // 1 hour
function isLocked(job) {
  if (!job.locked_by || !job.locked_at) return false;
  return (Date.now() - new Date(job.locked_at).getTime()) < LOCK_MS;
}

async function deleteJob(job, setJobs, setTotal) {
  if (!window.confirm(`Permanently delete Job #${job.job_number}? This cannot be undone.`)) return;
  try {
    await api.delete(`/jobs/${job.id}`);
    setJobs(prev => prev.filter(j => j.id !== job.id));
    setTotal(t => t - 1);
    toast.success(`Job #${job.job_number} deleted`);
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Delete failed');
  }
}

const NEW_JOB_FIELDS = [
  ['job_number',       'Job # *',           'text'],
  ['branch',           'Branch',            'text'],
  ['primary_tech',     'Tech / RSC',        'text'],
  ['job_type',         'Job Type',          'text'],
  ['business_unit',    'Business Unit',     'text'],
  ['scheduled_date',   'Scheduled Date',    'date'],
  ['customer_name',    'Customer Name',     'text'],
  ['customer_address', 'Customer Address',  'text'],
  ['jobs_total',       'Jobs Total ($)',    'number'],
  ['hours_worked',     'Hours Worked',      'number'],
];

function NewJobModal({ onClose, onCreated }) {
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function submit() {
    if (!form.job_number || !form.job_number.trim()) {
      toast.error('Job # is required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      ['jobs_total', 'hours_worked'].forEach(k => {
        if (payload[k] !== undefined && payload[k] !== '') payload[k] = Number(payload[k]);
        else delete payload[k];
      });
      const r = await api.post('/jobs/', payload);
      toast.success(`Job #${r.data.job_number} created`);
      onCreated(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create job');
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Add Job for Audit</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
          <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {NEW_JOB_FIELDS.map(([key, label, type]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                <input type={type} value={form[key] ?? ''}
                  onChange={e => set(key, e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Summary / Notes</label>
              <textarea rows={3} value={form.summary ?? ''}
                onChange={e => set('summary', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-500 text-sm rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={submit} disabled={saving}
              className="px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A] disabled:opacity-40">
              {saving ? 'Creating...' : 'Create Job'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const STATUS_MAP = {
  pending:  { label: 'Pending',  color: 'bg-amber-100 text-amber-700' },
  audited:  { label: 'Pass',     color: 'bg-green-100 text-green-700' },
  flagged:  { label: 'Flag',     color: 'bg-red-100 text-red-700' },
};

export default function AuditQueue() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const [jobs,     setJobs]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [branches, setBranches] = useState([]);
  const [showNewJob, setShowNewJob] = useState(false);

  const [filters, setFilters] = useState({
    branch: '', tech: '', status: '', search: ''
  });

  useEffect(() => {
    api.get('/jobs/branches').then(r => setBranches(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, per_page: 50 };
    if (filters.branch) params.branch  = filters.branch;
    if (filters.tech)   params.tech    = filters.tech;
    if (filters.status) params.status  = filters.status;
    if (filters.search) params.search  = filters.search;

    api.get('/jobs/', { params })
      .then(r => {
        setJobs(r.data.jobs);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .finally(() => setLoading(false));
  }, [filters, page]);

  function updateFilter(key, val) {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  }

  return (
    <div className="p-8 space-y-6">
      {showNewJob && (
        <NewJobModal
          onClose={() => setShowNewJob(false)}
          onCreated={(job) => { setJobs(j => [job, ...j]); setTotal(t => t + 1); }}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Queue</h1>
          <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} jobs total</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'auditor') && (
          <button onClick={() => setShowNewJob(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A] transition-colors">
            <Plus size={16} /> Add Job
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search job #, customer, tech..."
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
            />
          </div>
          <select
            value={filters.branch}
            onChange={e => updateFilter('branch', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
          >
            <option value="">All Branches</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            value={filters.status}
            onChange={e => updateFilter('status', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="audited">Passed</option>
            <option value="flagged">Flagged</option>
          </select>
          <button
            onClick={() => { setFilters({ branch:'', tech:'', status:'', search:'' }); setPage(1); }}
            className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Job table — grouped by branch → tech, single table per branch for column alignment */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No jobs found</div>
          ) : (() => {
            const branchMap = {};
            jobs.forEach(job => {
              const b = job.branch || 'Unknown';
              const t = job.primary_tech || 'Unknown';
              if (!branchMap[b]) branchMap[b] = {};
              if (!branchMap[b][t]) branchMap[b][t] = [];
              branchMap[b][t].push(job);
            });
            const COLS = ['Job #','Job Type','Sched Date','Customer','Jobs Total','Status','Score','Action'];
            return Object.entries(branchMap).map(([branch, techs]) => (
              <div key={branch}>
                {/* Branch header */}
                <div className="bg-[#3D2B1A] text-white px-4 py-2 text-xs font-bold tracking-wider uppercase">
                  {branch}
                </div>
                {/* Single table per branch — columns align */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {COLS.map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(techs).map(([tech, techJobs]) => (
                      <>
                        {/* Tech name row — plain, light */}
                        <tr key={`tech-${tech}`} className="bg-gray-100 border-t border-b border-gray-200">
                          <td colSpan={COLS.length} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                            {tech}
                            <span className="ml-2 font-normal text-gray-400">{techJobs.length} job{techJobs.length !== 1 ? 's' : ''}</span>
                          </td>
                        </tr>
                        {techJobs.map((job, i) => {
                          const st = STATUS_MAP[job.audit_status] || STATUS_MAP.pending;
                          return (
                            <tr key={job.id} className={`border-b border-gray-50 hover:bg-orange-50/40 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                              <td className="px-4 py-2.5 font-mono text-[#E8761A] text-xs">{job.job_number}</td>
                              <td className="px-4 py-2.5 text-gray-500 text-xs">{job.job_type}</td>
                              <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                                {job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 truncate max-w-[160px]">{job.customer_name || '—'}</td>
                              <td className="px-4 py-2.5 text-gray-600 text-right">
                                {job.jobs_total ? `$${Number(job.jobs_total).toLocaleString()}` : '—'}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {job.score != null
                                  ? <span className={`text-xs font-semibold ${job.score >= 0.9 ? 'text-green-600' : job.score >= 0.75 ? 'text-amber-600' : 'text-red-600'}`}>
                                      {Math.round(job.score * 100)}%
                                    </span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                {isLocked(job) && job.locked_by !== user?.id ? (
                                  <span title={`Being audited by ${job.locked_by_name || 'another user'}`}
                                    className="px-3 py-1 text-xs font-semibold rounded-lg bg-yellow-100 text-yellow-700 border border-yellow-300 cursor-default">
                                    In Progress
                                  </span>
                                ) : (
                                  <button onClick={async () => {
                                    try {
                                      await api.post(`/jobs/${job.id}/lock`);
                                      navigate(`/audit/${job.id}`);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Could not open audit');
                                    }
                                  }}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-[#3D2B1A] text-white hover:bg-[#E8761A] transition-colors">
                                    {isLocked(job) && job.locked_by === user?.id ? 'Resume' :
                                     job.audit_status === 'pending' ? 'Audit' : 'Review'}
                                  </button>
                                )}
                                {user?.role === 'admin' && (
                                  <button onClick={() => deleteJob(job, setJobs, setTotal)}
                                    title="Delete job"
                                    className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            ));
          })()}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {pages}</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p-1)}
                className="px-3 py-1 rounded border disabled:opacity-30 hover:bg-gray-50">Prev</button>
              <button disabled={page === pages} onClick={() => setPage(p => p+1)}
                className="px-3 py-1 rounded border disabled:opacity-30 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
